import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

// A printable weekly activity rota: portrait A4, one column per chosen day,
// split into an Afternoon (2-4pm) and an Evening (7-9pm) section. Each cell
// lists that day/slot's activity groups as "group name — leader" with the
// group's notes underneath. Mirrors the paper rota camps were printing by hand.

const BRAND_NAVY = [26, 31, 113];
const GREY = [110, 110, 110];

// Cell text metrics (points). Draw increments MUST match the measure sums so
// the row heights we reserve line up with what we paint in didDrawCell.
const PAD = 6;
const TITLE_SIZE = 11;
const TITLE_LH = 13.5;
const NOTE_SIZE = 9;
const NOTE_LH = 11;

// Name resolution mirrors activityRegistersPdf.js: prefer the value cached on
// the group, else resolve from the per-day reference data, else a placeholder.
function leaderNameOf(group, leaders) {
  if (group?.leaderName) return group.leaderName;
  const match = (leaders || []).find(
    (l) => String(l.staffId) === String(group?.leaderStaffId)
  );
  return match?.name || "";
}

function activityNameOf(group, products) {
  if (group?.productName) return group.productName;
  const match = (products || []).find(
    (p) => String(p.productId) === String(group?.productId)
  );
  return match?.productName || "";
}

// Join staff names the way the paper rota does: "Jack", "Jack and Ellie",
// "Ellie, Conor and Will".
function joinNames(names) {
  const list = names.filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

// The rota lists staff by first name only ("Will Matthews" -> "Will").
function firstNameOf(full) {
  return String(full || "").trim().split(/\s+/)[0] || "";
}

// The staff on an activity: the leader's first name plus the first names of any
// ADULT group members (helpers), deduped so a leader also listed as a member
// only appears once. The "(Lead)" tag is applied later, and only when there's
// more than one name to distinguish the lead from.
function staffNamesOf(group, leaders) {
  const leader = firstNameOf(leaderNameOf(group, leaders));
  const adultMembers = (Array.isArray(group?.members) ? group.members : [])
    .filter((m) => String(m?.personType).toUpperCase() === "ADULT")
    .map((m) => firstNameOf(m?.name))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const seen = new Set();
  const others = [];
  if (leader) seen.add(leader.toLowerCase());
  adultMembers.forEach((name) => {
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    others.push(name);
  });
  return { leader, others };
}

// A weekend group that grew too big is split into per-staff sub-groups named
// `${base} — ${staffFullName}` (see handleSplitGroup), each sharing the parent's
// productId. On the rota we recombine them into the single parent activity.
const SPLIT_RE = /^(.+?) — (.+)$/;

// Fold split sub-groups back into their parent. Returns [{ base, children }],
// preserving the input order of the parent/standalone groups.
function combineWeekendSplits(groups) {
  const list = (Array.isArray(groups) ? groups : []).filter(Boolean);
  const byName = new Map();
  list.forEach((g) => {
    const nm = String(g?.name || "").trim();
    if (nm && !byName.has(nm)) byName.set(nm, g);
  });

  const childrenByBase = new Map();
  const childSet = new Set();
  list.forEach((g) => {
    const m = String(g?.name || "").trim().match(SPLIT_RE);
    if (!m) return;
    const baseName = m[1].trim();
    const baseGroup = byName.get(baseName);
    // Only fold in when a real parent exists AND shares the product — otherwise
    // it's a coincidental " — " in the name (e.g. a separate bespoke group).
    if (!baseGroup || baseGroup === g) return;
    if (baseGroup.productId == null) return;
    if (String(baseGroup.productId) !== String(g.productId ?? null)) return;
    if (!childrenByBase.has(baseName)) childrenByBase.set(baseName, []);
    childrenByBase.get(baseName).push(g);
    childSet.add(g);
  });

  const combined = [];
  list.forEach((g) => {
    if (childSet.has(g)) return;
    const nm = String(g?.name || "").trim();
    combined.push({ base: g, children: childrenByBase.get(nm) || [] });
  });
  return combined;
}

// The cell headline: the group's own (free-text) name if it has one — that's
// where camps write "Discovery – Stirling, Greeks and Irene" — else the product
// name. Then the leader (tagged Lead), other staff, and any split sub-group
// leaders, appended after an em dash.
function combinedTitleOf(base, children, leaders, products) {
  const displayName =
    String(base?.name || "").trim() ||
    activityNameOf(base, products) ||
    "Activity";

  const { leader, others } = staffNamesOf(base, leaders);
  const rest = [...others];
  const seen = new Set(rest.map((n) => n.toLowerCase()));
  if (leader) seen.add(leader.toLowerCase());
  // Each split sub-group's leader becomes an additional (non-lead) staff name.
  (children || []).forEach((c) => {
    const cl = firstNameOf(leaderNameOf(c, leaders));
    if (cl && !seen.has(cl.toLowerCase())) {
      seen.add(cl.toLowerCase());
      rest.push(cl);
    }
  });

  // Only mark the lead when there's more than one name in play.
  const total = (leader ? 1 : 0) + rest.length;
  const leaderDisplay = leader && total > 1 ? `${leader} (Lead)` : leader;
  const names = [leaderDisplay, ...rest].filter(Boolean);

  const staff = joinNames(names);
  return staff ? `${displayName} — ${staff}` : displayName;
}

function buildItems(groups, leaders, products) {
  return combineWeekendSplits(groups)
    .map(({ base, children }) => ({
      title: combinedTitleOf(base, children, leaders, products),
      notes: String(base?.notes || "").trim(),
    }))
    .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
}

// Height reserved for a single activity (one bordered box): its title lines
// plus any notes lines, with padding.
function measureItem(doc, item, innerW) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TITLE_SIZE);
  let content = doc.splitTextToSize(item.title, innerW).length * TITLE_LH;
  if (item.notes) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(NOTE_SIZE);
    content += doc.splitTextToSize(item.notes, innerW).length * NOTE_LH;
  }
  return content + PAD * 2;
}

function drawItem(doc, item, cell, innerW) {
  let y = cell.y + PAD;
  const x = cell.x + PAD;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TITLE_SIZE);
  doc.setTextColor(...BRAND_NAVY);
  doc.splitTextToSize(item.title, innerW).forEach((line) => {
    y += TITLE_LH;
    doc.text(line, x, y - 2);
  });
  if (item.notes) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(NOTE_SIZE);
    doc.setTextColor(...GREY);
    doc.splitTextToSize(item.notes, innerW).forEach((line) => {
      y += NOTE_LH;
      doc.text(line, x, y - 2);
    });
  }
}

function renderSection(doc, { sectionTitle, startY, itemsByCol, marginX, cellWidth, headLabels }) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...BRAND_NAVY);
  doc.text(sectionTitle, marginX, startY);

  const innerW = cellWidth - PAD * 2;
  // One body row per activity "slot": row r, column c is the c-th day's r-th
  // activity (or empty), so every group gets its own bordered box like the
  // paper rota.
  const rowCount = Math.max(1, ...itemsByCol.map((items) => items.length));
  const body = Array.from({ length: rowCount }, () => headLabels.map(() => ""));
  const itemAt = (r, c) => itemsByCol[c] && itemsByCol[c][r];

  const columnStyles = {};
  headLabels.forEach((_, i) => {
    columnStyles[i] = { cellWidth };
  });

  autoTable(doc, {
    startY: startY + 6,
    margin: { left: marginX, right: marginX },
    head: [headLabels],
    body,
    styles: {
      valign: "top",
      cellPadding: PAD,
      lineColor: [200, 200, 200],
      lineWidth: 0.5,
      fontSize: TITLE_SIZE,
    },
    headStyles: {
      fillColor: BRAND_NAVY,
      textColor: 255,
      fontStyle: "bold",
      halign: "center",
      fontSize: 11,
      cellPadding: 6,
    },
    columnStyles,
    // Reserve height for the one activity in this cell and suppress autoTable's
    // own text; we paint the rich (bold title + grey notes) content ourselves.
    didParseCell: (data) => {
      if (data.section === "body") {
        data.cell.text = [];
        const item = itemAt(data.row.index, data.column.index);
        data.cell.styles.minCellHeight = item
          ? Math.max(30, measureItem(doc, item, innerW))
          : 30;
      }
    },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      const item = itemAt(data.row.index, data.column.index);
      if (item) drawItem(doc, item, data.cell, innerW);
    },
  });

  return doc.lastAutoTable.finalY;
}

// days: [{ dateStr, afternoon: Group[], evening: Group[], leaders: Leader[] }]
export function downloadWeeklyRotaPdf({ campus, days = [], products = [] }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 28;
  const n = days.length || 1;
  const cellWidth = (pageW - marginX * 2) / n;

  let y = 44;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...BRAND_NAVY);
  doc.text("Activity Rota", marginX, y);
  y += 18;

  const first = days[0]?.dateStr;
  const last = days[days.length - 1]?.dateStr;
  const range = first
    ? `${dayjs(first).format("ddd D MMM")} – ${dayjs(last).format("ddd D MMM YYYY")}`
    : "";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  doc.text([campus, range].filter(Boolean).join("  •  "), marginX, y);
  y += 14;

  const headLabels = days.map(
    (d) => `${dayjs(d.dateStr).format("dddd")}\n${dayjs(d.dateStr).format("D MMM")}`
  );
  const afternoonByCol = days.map((d) => buildItems(d.afternoon, d.leaders, products));
  const eveningByCol = days.map((d) => buildItems(d.evening, d.leaders, products));

  const afternoonEnd = renderSection(doc, {
    sectionTitle: "Afternoon Rota 2–4pm",
    startY: y + 8,
    itemsByCol: afternoonByCol,
    marginX,
    cellWidth,
    headLabels,
  });

  renderSection(doc, {
    sectionTitle: "Evening Rota 7–9pm",
    startY: afternoonEnd + 22,
    itemsByCol: eveningByCol,
    marginX,
    cellWidth,
    headLabels,
  });

  const safeCampus = String(campus || "campus").replace(/[^\w-]+/g, "_");
  doc.save(`Activity_Rota_${safeCampus}_${first || ""}.pdf`);
}
